export type SettingsWorkspaceNavigationDetails = {
  externallyManagedIntegration?: {
    canUpdateInternalAssetContentSecurity: boolean;
    canUpdateWorkspaceName: boolean;
    integration?: 'WORKFRONT' | null;
  } | null;
  id: string;
  images?: {
    image32?: string | null;
    image64?: string | null;
    image128?: string | null;
    original?: string | null;
  } | null;
  isWorkfront?: boolean | null;
  name: string;
  permissions?: {
    canDelete: boolean;
    canManageResourceControlPolicies: boolean;
    canUpdate: boolean;
  } | null;
};

export type SettingsScopeNavigationInput =
  | {
      accountId: string;
      type: 'account' | 'personal';
    }
  | {
      accountId: string;
      type: 'workspace';
      workspace?: SettingsWorkspaceNavigationDetails;
      workspaceId: string;
    };

type OpenWorkspaceSettingsInput = {
  accountId: string;
  workspaceId: string;
};

export const WORKSPACE_SETTINGS_PATH = '/settings/workspace/branding';

type SettingsScopeRequestDelivery = 'live' | 'pending';

// This only covers loading the destination route. The provider claims the
// request before loading scope data and rejects replays on unrelated routes.
const PENDING_SETTINGS_SCOPE_TTL_MS = 60_000;

let pendingSettingsScope: SettingsScopeNavigationInput | undefined;
let pendingSettingsScopeExpiresAt = 0;
let currentSettingsScope: SettingsScopeNavigationInput | undefined;
const settingsScopeListeners = new Set<
  (
    input: SettingsScopeNavigationInput,
    delivery: SettingsScopeRequestDelivery
  ) => void
>();
const currentSettingsScopeListeners = new Set<
  (input: SettingsScopeNavigationInput | undefined) => void
>();

export function setCurrentSettingsScope(
  input: SettingsScopeNavigationInput | undefined
): void {
  currentSettingsScope = input;
  currentSettingsScopeListeners.forEach((listener) => listener(input));
}

export function getCurrentSettingsScope():
  | SettingsScopeNavigationInput
  | undefined {
  return currentSettingsScope;
}

export function subscribeToCurrentSettingsScope(
  listener: (input: SettingsScopeNavigationInput | undefined) => void
): () => void {
  currentSettingsScopeListeners.add(listener);
  listener(currentSettingsScope);

  return () => currentSettingsScopeListeners.delete(listener);
}

export function setPendingSettingsScope(
  input: SettingsScopeNavigationInput
): void {
  // Keep the request until a provider applies it. A listener can unmount
  // during browser navigation after receiving the request but before using it.
  pendingSettingsScope = input;
  pendingSettingsScopeExpiresAt = Date.now() + PENDING_SETTINGS_SCOPE_TTL_MS;
  settingsScopeListeners.forEach((listener) => listener(input, 'live'));
}

export function peekPendingSettingsScope():
  | SettingsScopeNavigationInput
  | undefined {
  if (pendingSettingsScope && Date.now() >= pendingSettingsScopeExpiresAt) {
    pendingSettingsScope = undefined;
    pendingSettingsScopeExpiresAt = 0;
  }

  return pendingSettingsScope;
}

export function consumePendingSettingsScope():
  | SettingsScopeNavigationInput
  | undefined {
  const pending = peekPendingSettingsScope();
  pendingSettingsScope = undefined;
  pendingSettingsScopeExpiresAt = 0;
  return pending;
}

export function acknowledgeSettingsScopeRequest(
  input: SettingsScopeNavigationInput
): void {
  // Reference identity prevents an older request from acknowledging a newer
  // request for the same scope.
  if (pendingSettingsScope === input) {
    pendingSettingsScope = undefined;
    pendingSettingsScopeExpiresAt = 0;
  }
}

export function subscribeToSettingsScopeRequests(
  listener: (
    input: SettingsScopeNavigationInput,
    delivery: SettingsScopeRequestDelivery
  ) => void
): () => void {
  settingsScopeListeners.add(listener);

  const pending = peekPendingSettingsScope();
  if (pending) {
    listener(pending, 'pending');
  }

  return () => settingsScopeListeners.delete(listener);
}

export function setPendingWorkspaceSettings(
  input: OpenWorkspaceSettingsInput
): void {
  setPendingSettingsScope({ ...input, type: 'workspace' });
}
